/**
 * StudentAssignmentPanel.tsx
 * 학생 검색 및 좌석 배정 패널
 *
 * Features:
 * - 학생 검색 (Debounced)
 * - 검색 결과 리스트
 * - 좌석 선택 UI (FloorSelector + SeatCanvas)
 * - 배정 확인 모달
 * - 배정 히스토리
 */

import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSeatAssignment } from '../../hooks/useSeatAssignment';
import SeatAssignmentModal from './SeatAssignmentModal';
import FloorSelector from './FloorSelector';
import SeatCanvas from './SeatCanvas';
import { apiClient } from '../../../../infrastructure/api/axiosConfig';
import { Seat } from '../../repositories/SeatLayoutRepository';

interface Floor {
  id: string;
  name: string;
  canvas_width: number;
  canvas_height: number;
}

interface StudentAssignmentPanelProps {
  organizationId: string;
}

export const StudentAssignmentPanel: React.FC<StudentAssignmentPanelProps> = ({
  organizationId,
}) => {
  const {
    loading,
    students,
    selectedStudent,
    seatStatus,
    assignmentHistory,
    searchStudents,
    selectStudent,
    assignSeat,
    releaseSeat,
    reset,
  } = useSeatAssignment();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchStudents(searchQuery);
      } else {
        searchStudents('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, searchStudents]);

  // Fetch floors
  useEffect(() => {
    const fetchFloors = async () => {
      try {
        const response = await apiClient.get<Floor[]>(
          `/api/v1/organizations/${organizationId}/floors`
        );
        setFloors(response.data);
        if (response.data.length > 0 && !selectedFloorId) {
          setSelectedFloorId(response.data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch floors:', error);
      }
    };

    if (organizationId) {
      fetchFloors();
    }
  }, [organizationId, selectedFloorId]);

  // Fetch seats when floor changes
  useEffect(() => {
    const fetchSeats = async () => {
      if (!selectedFloorId) return;

      try {
        const response = await apiClient.get<Seat[]>(
          `/api/v1/floors/${selectedFloorId}/seats`
        );
        setSeats(response.data);
      } catch (error) {
        console.error('Failed to fetch seats:', error);
      }
    };

    fetchSeats();
  }, [selectedFloorId]);

  const handleStudentSelect = useCallback(
    (studentId: string) => {
      const student = students.find(s => s.id === studentId);
      if (student) {
        selectStudent(student);
      }
    },
    [students, selectStudent]
  );

  const handleSeatClick = useCallback((seatId: string) => {
    if (!seatId) {
      setSelectedSeatId(null);
    } else {
      setSelectedSeatId(seatId);
    }
  }, []);

  const handleAssignClick = useCallback(() => {
    if (!selectedStudent || !selectedSeatId) {
      return;
    }
    setIsModalOpen(true);
  }, [selectedStudent, selectedSeatId]);

  const handleConfirmAssign = useCallback(
    async (autoReleasePrevious: boolean) => {
      if (!selectedSeatId) return;

      const success = await assignSeat(selectedSeatId.toString(), autoReleasePrevious);
      if (success) {
        setIsModalOpen(false);
        setSelectedSeatId(null);
        const updatedSeats = await apiClient.get<Seat[]>(
          `/api/v1/floors/${selectedFloorId}/seats`
        );
        setSeats(updatedSeats.data);
      }
    },
    [selectedSeatId, selectedFloorId, assignSeat]
  );

  const handleReleaseSeat = useCallback(async () => {
    const confirmed = window.confirm(
      `${selectedStudent?.name}님의 좌석을 해제하시겠습니까?`
    );
    if (confirmed) {
      const success = await releaseSeat();
      if (success && selectedFloorId) {
        const updatedSeats = await apiClient.get<Seat[]>(
          `/api/v1/floors/${selectedFloorId}/seats`
        );
        setSeats(updatedSeats.data);
      }
    }
  }, [selectedStudent, selectedFloorId, releaseSeat]);

  const selectedSeat = seats.find(s => s.id === selectedSeatId);
  const currentFloor = floors.find(f => f.id === selectedFloorId);

  const selectedSeatInfo = selectedSeat && currentFloor
    ? {
        seat_id: selectedSeat.id.toString(),
        seat_code: selectedSeat.seat_number,
        floor_name: currentFloor.name,
      }
    : null;

  const currentSeatInfo = seatStatus
    ? {
        seat_code: seatStatus.seat_code,
        floor_name: seatStatus.floor_name,
      }
    : null;

  return (
    <div className="h-full flex gap-6">
      {/* Left Panel - Student Search */}
      <div className="w-96 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="학생 이름 또는 전화번호로 검색"
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-gray-500">검색 중...</div>
            </div>
          )}

          {!loading && students.length === 0 && searchQuery.length >= 2 && (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-gray-500">검색 결과가 없습니다</div>
            </div>
          )}

          {!loading && students.length === 0 && searchQuery.length < 2 && (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-gray-500">2글자 이상 입력해주세요</div>
            </div>
          )}

          {!loading && students.length > 0 && (
            <div className="divide-y divide-gray-100">
              {students.map(student => (
                <button
                  key={student.id}
                  onClick={() => handleStudentSelect(student.id)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors duration-150 ${
                    selectedStudent?.id === student.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {student.profile_image_url ? (
                      <img
                        src={student.profile_image_url}
                        alt={student.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-gray-400" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{student.name}</div>
                      <div className="text-sm text-gray-500">{student.phone}</div>
                      {student.current_seat_code && (
                        <div className="text-xs text-blue-600 mt-1">
                          현재: {student.current_seat_code}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Student Info */}
        {selectedStudent && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-2">선택된 학생</div>
            <div className="flex items-center gap-3 mb-3">
              {selectedStudent.profile_image_url ? (
                <img
                  src={selectedStudent.profile_image_url}
                  alt={selectedStudent.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <UserCircleIcon className="w-12 h-12 text-gray-400" />
              )}
              <div>
                <div className="font-medium text-gray-900">{selectedStudent.name}</div>
                <div className="text-sm text-gray-500">{selectedStudent.phone}</div>
              </div>
            </div>

            {seatStatus && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 mb-1">현재 좌석</div>
                <div className="flex items-center justify-between py-2 px-3 bg-white rounded border border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {seatStatus.floor_name} - {seatStatus.seat_code}
                  </span>
                  <button
                    onClick={handleReleaseSeat}
                    disabled={loading}
                    className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    해제
                  </button>
                </div>
              </div>
            )}

            {assignmentHistory.length > 0 && (
              <div>
                <div className="text-xs text-gray-600 mb-1">최근 배정</div>
                <div className="space-y-1">
                  {assignmentHistory.slice(0, 3).map((history, index) => (
                    <div
                      key={index}
                      className="text-xs text-gray-500 py-1 px-2 bg-white rounded"
                    >
                      {history.floor_name} - {history.seat_code}
                      <span className="ml-2 text-gray-400">
                        {new Date(history.assigned_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Panel - Seat Selection */}
      <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">좌석 선택</h3>
            {selectedStudent && selectedSeatId && (
              <button
                onClick={handleAssignClick}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-150 disabled:opacity-50"
              >
                배정하기
              </button>
            )}
          </div>

          <FloorSelector
            organizationId={organizationId}
            selectedFloorId={selectedFloorId}
            onFloorChange={setSelectedFloorId}
          />
        </div>

        <div className="flex-1 overflow-auto p-4">
          {selectedFloorId && currentFloor ? (
            <SeatCanvas
              seats={seats}
              canvasWidth={currentFloor.canvas_width}
              canvasHeight={currentFloor.canvas_height}
              snapGrid={20}
              onSeatMove={() => {}}
              onSeatClick={handleSeatClick}
              selectedSeatId={selectedSeatId ?? undefined}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-gray-500">층을 선택해주세요</div>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      <SeatAssignmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        student={selectedStudent}
        selectedSeat={selectedSeatInfo}
        currentSeat={currentSeatInfo}
        onConfirm={handleConfirmAssign}
        loading={loading}
      />
    </div>
  );
};

export default StudentAssignmentPanel;
